/* eslint-disable @typescript-eslint/no-explicit-any */
import { withStyles } from "@mui/styles";
import { createStyles, styled } from "@mui/material/styles";
import {
  Avatar,
  Badge,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Article,
  ChevronLeft,
  Home,
  Person,
  TextSnippet,
  Description,
  DevicesOther,
  Build
} from "@mui/icons-material";
import CategoryIcon from "@mui/icons-material/Category";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { CSSObject } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import { MenuContext } from "../Context/menuContext";
import { Vocabulary } from "../Utils/Vocabulary";
import { useNavigate } from "react-router-dom";
import { LocalUrlEnum } from "../Utils/Utils";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentIcon from "@mui/icons-material/Payment";
import theme from "../Theme/Theme";
import moment from "moment";
import { BadgeContext } from "../Context/badgeContext";
import { roles, usersTypes } from "../Utils/Constants";

type Props = {
  classes: any;
};

const drawerWidth = 240;

/**
 *
 * @param theme
 * @returns
 */
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

/**
 *
 * @param theme
 * @returns
 */
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 30px)`,
  },
});

/**
 *
 */
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  height: "79px",
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

/**
 *
 */
const CustomDrawer = styled(Drawer)(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function DrawerLayout(props: Props) {
  const { classes } = props;
  const badgeContext = useContext(BadgeContext);
  const menuContext = useContext(MenuContext);
  const navigate = useNavigate();
  const localStorageRole = localStorage.getItem("role");
  const [openCollapse, setOpenCollapse] = useState("");
  const matches = useMediaQuery("(max-width:850px)");

  // On mobile, the admin/staff drawer is a temporary overlay rather than a
  // permanent sidebar (see the render below), so it should start closed
  // instead of inheriting the desktop-oriented "open" default.
  useEffect(() => {
    if (matches) {
      menuContext.setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  /**
   * change the value of open collapse to the text of the button
   * @param newValue
   */
  function handleOpenCollapse(newValue: string) {
    setOpenCollapse(openCollapse === newValue ? "" : newValue);
  }
  /**
   *
   * @param path
   */
  function handleNavigationChange(path: string) {
    navigate(path);
    // Close the temporary mobile drawer after navigating, same as any
    // standard mobile nav overlay; the permanent desktop sidebar is
    // unaffected since this only runs when matches is true.
    if (matches) {
      menuContext.setOpen(false);
    }
  }

  /**
   *
   * @returns
   */
  function showMenu() {
    switch (localStorageRole) {
      case roles.admin:
      case roles.serviceTechnician:
      case roles.user:
        return AdminMainSliderData();
      case roles.customer:
        return CustomerMainSliderData();
      default:
        return null;
    }
  }
  /**
   *
   */
  function CustomerMainSliderDataMobile() {
    return (
      <List
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          display: "flex",
          flexDirection: "row",
          backgroundColor: theme.palette.dashboard?.main,
          borderTop: `1px solid ${theme.palette.divider}`,
          zIndex: 1300,
          padding: "5px 0",
        }}
      >
        {/* orders */}
        <ListItem
          className={classes.li}
          onClick={(event) =>
            handleNavigationChange(`${LocalUrlEnum.customerOrders}`)
          }
        >
          <ListItemButton
            sx={{
              justifyContent: "center",
            }}
          >
            <ListItemIcon
              sx={{
                justifyContent: "center",
              }}
            >
              <Description
                sx={{ color: theme.palette.primary?.main }}
                fontSize="large"
              />
               <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.orders}
                  </Typography>
                }
              />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        {/* account settings */}
        <ListItem
          className={classes.li}
          onClick={(event) =>
            handleNavigationChange(`${LocalUrlEnum.accountSettings}`)
          }
        >
          <ListItemButton
            sx={{
              justifyContent: "center",
            }}
          >
            <ListItemIcon
              sx={{
                justifyContent: "center",
              }}
            >
              <SettingsIcon
                sx={{ color: theme.palette.primary?.main }}
                fontSize="large"
              />
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.accountSettings}
                  </Typography>
                }
              />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </List>
    );
  }

  /**
   *
   */
  function CustomerMainSliderData() {
    return (
      <>
        <DrawerHeader>
          <IconButton
            onClick={() => {
              menuContext.setOpen(!menuContext.open);
            }}
          >
            <Avatar className={classes.avatar}>
              <ChevronLeft
                sx={{ color: theme.palette.whiteColor?.main }}
                fontSize="large"
              />
            </Avatar>
          </IconButton>
        </DrawerHeader>
        <List>
          {/* orders */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(`${LocalUrlEnum.customerOrders}`)
            }
          >
            <ListItemButton
              sx={{
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <Description
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.orders}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
          {/* account settings */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(`${LocalUrlEnum.accountSettings}`)
            }
          >
            <ListItemButton
              sx={{
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <SettingsIcon
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.accountSettings}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </>
    );
  }
  /**
   *
   * @returns
   */
  function AdminMainSliderData() {
    return (
      <>
        <DrawerHeader>
          <IconButton
            onClick={() => {
              menuContext.setOpen(!menuContext.open);
            }}
          >
            <Avatar className={classes.avatar}>
              <ChevronLeft
                sx={{ color: theme.palette.whiteColor?.main }}
                fontSize="large"
              />
            </Avatar>
          </IconButton>
        </DrawerHeader>
        <List>
          {/* dashboard */}
          <ListItem
            className={classes.li}
            onClick={(event) => handleNavigationChange("/")}
          >
            <ListItemButton
              sx={{
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <Home
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.dashboard}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
          {/* reports */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(`${LocalUrlEnum.reports}`)
            }
          >
            <ListItemButton
              sx={{
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <AnalyticsIcon
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.reports}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
          {/* orders */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(
                `${
                  LocalUrlEnum.orders
                }?page=1?perPage=50?filter=Niciun filtru?sort=createdAt,desc?startDate=${moment()
                  .subtract(60, "d")
                  .format("YYYY-MM-DD")}?endDate=${moment().format(
                  "YYYY-MM-DD"
                )}`
              )
            }
          >
            <ListItemButton
              sx={{
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <Description
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Badge badgeContent={badgeContext.ordersNumber} color="error">
                    <Typography className={classes.text} variant="h6">
                      {Vocabulary.orders}
                    </Typography>
                  </Badge>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
          {/* devices */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(
                `${LocalUrlEnum.devices}?page=1?perPage=10`
              )
            }
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <DevicesOther
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.devices}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
          {/* questionnaires */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(
                `${LocalUrlEnum.questionnaires}?page=1?perPage=10`
              )
            }
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <Article
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.questionnaires}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>
          {/* users */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(
                `${LocalUrlEnum.users}?type=${usersTypes.employees}?page=1?perPage=10`
              )
            }
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <Person
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.users}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>

          {/* templates */}
          <ListItem
            className={classes.li}
            sx={{ display: "block" }}
            onClick={() => handleOpenCollapse(Vocabulary.templates)}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <TextSnippet
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.templates}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
              {openCollapse === Vocabulary.templates ? (
                <KeyboardArrowUpIcon
                  style={{
                    fontSize: "25px",
                    margin: 10,
                    color: theme.palette.whiteColor?.main,
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  style={{
                    fontSize: "25px",
                    margin: 10,
                    color: theme.palette.whiteColor?.main,
                  }}
                />
              )}
            </ListItemButton>
            <div>
              <Collapse
                in={openCollapse === Vocabulary.templates}
                unmountOnExit
              >
                <List
                  sx={{
                    paddingLeft: "55px",
                    color: theme.palette.whiteColor?.main,
                    background: theme.palette.dashboardDark?.main,
                    boxShadow: "inset 0 0 25px #00000033",
                  }}
                >
                  <ListItem
                    button
                    onClick={() =>
                      handleNavigationChange(
                        `${LocalUrlEnum.questionnairesTemplates}?page=1?perPage=10`
                      )
                    }
                  >
                    <ListItemText>{Vocabulary.questionnaires}</ListItemText>
                  </ListItem>
                  <ListItem
                    button
                    onClick={() =>
                      handleNavigationChange(
                        `${LocalUrlEnum.emailTemplates}?page=1?perPage=10`
                      )
                    }
                  >
                    <ListItemText>{Vocabulary.email}</ListItemText>
                  </ListItem>
                  <ListItem
                    button
                    onClick={() =>
                      handleNavigationChange(
                        `${LocalUrlEnum.documentTemplates}?page=1?perPage=10`
                      )
                    }
                  >
                    <ListItemText>{Vocabulary.documents}</ListItemText>
                  </ListItem>
                </List>
              </Collapse>
            </div>
          </ListItem>
          {/* status order */}
          <ListItem
            className={classes.li}
            onClick={(event) =>
              handleNavigationChange(
                `${LocalUrlEnum.orderStatus}?page=1?perPage=10`
              )
            }
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <BookmarkBorderIcon
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.orderStatus}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>

          {/* settings */}
          <ListItem
            className={classes.li}
            sx={{ display: "block" }}
            onClick={() => handleOpenCollapse(Vocabulary.administration)}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: menuContext.open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: menuContext.open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <SettingsIcon
                  sx={{ color: theme.palette.primary?.main }}
                  fontSize="large"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.administration}
                  </Typography>
                }
                sx={{ opacity: menuContext.open ? 1 : 0 }}
              />
              {openCollapse === Vocabulary.templates ? (
                <KeyboardArrowUpIcon
                  style={{
                    fontSize: "25px",
                    margin: 10,
                    color: theme.palette.whiteColor?.main,
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  style={{
                    fontSize: "25px",
                    margin: 10,
                    color: theme.palette.whiteColor?.main,
                  }}
                />
              )}
            </ListItemButton>
            <div>
              <Collapse
                in={openCollapse === Vocabulary.administration}
                unmountOnExit
              >
                {/* trashed orders */}
                <List
                  sx={{
                    color: theme.palette.whiteColor?.main,
                    background: theme.palette.dashboardDark?.main,
                    boxShadow: "inset 0 0 25px #00000033",
                  }}
                >
                  <ListItem
                    button
                    onClick={(event) =>
                      handleNavigationChange(
                        `${LocalUrlEnum.trashedOrders}?page=1?perPage=10`
                      )
                    }
                  >
                    <ListItemButton>
                      <RestoreFromTrashIcon
                        sx={{ color: theme.palette.primary?.main }}
                        fontSize="medium"
                      />
                      <ListItemText style={{ marginLeft: 10 }}>
                        {Vocabulary.trashedOrders}
                      </ListItemText>
                    </ListItemButton>
                  </ListItem>
                </List>
                {/* logs */}
                <List
                  sx={{
                    color: theme.palette.whiteColor?.main,
                    background: theme.palette.dashboardDark?.main,
                    boxShadow: "inset 0 0 25px #00000033",
                  }}
                >
                  <ListItem
                    button
                    onClick={(event) =>
                      handleNavigationChange(
                        `${
                          LocalUrlEnum.logs
                        }?page=1?perPage=10?startDate=${moment()
                          .subtract(14, "d")
                          .format("YYYY-MM-DD")}?endDate=${moment().format(
                          "YYYY-MM-DD"
                        )}`
                      )
                    }
                  >
                    <ListItemButton>
                      <BookmarkIcon
                        sx={{ color: theme.palette.primary?.main }}
                        fontSize="medium"
                      />
                      <ListItemText style={{ marginLeft: 10 }}>
                        {Vocabulary.logs}
                      </ListItemText>
                    </ListItemButton>
                  </ListItem>
                </List>
                {/* categories */}
                <List
                  sx={{
                    color: theme.palette.whiteColor?.main,
                    background: theme.palette.dashboardDark?.main,
                    boxShadow: "inset 0 0 25px #00000033",
                  }}
                >
                  <ListItem
                    button
                    onClick={(event) =>
                      handleNavigationChange(
                        `${LocalUrlEnum.categories}?page=1?perPage=10`
                      )
                    }
                  >
                    <ListItemButton>
                      <CategoryIcon
                        sx={{ color: theme.palette.primary?.main }}
                        fontSize="medium"
                      />
                      <ListItemText style={{ marginLeft: 10 }}>
                        {Vocabulary.categories}
                      </ListItemText>
                    </ListItemButton>
                  </ListItem>
                </List>
                {/* payment types */}
                <List
                  sx={{
                    color: theme.palette.whiteColor?.main,
                    background: theme.palette.dashboardDark?.main,
                    boxShadow: "inset 0 0 25px #00000033",
                  }}
                >
                  <ListItem
                    button
                    onClick={(event) =>
                      handleNavigationChange(
                        `${LocalUrlEnum.paymentTypes}?page=1?perPage=10`
                      )
                    }
                  >
                    <ListItemButton>
                      <PaymentIcon
                        sx={{ color: theme.palette.primary?.main }}
                        fontSize="medium"
                      />
                      <ListItemText style={{ marginLeft: 10 }}>
                        {Vocabulary.paymentTypes}
                      </ListItemText>
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </div>
          </ListItem>
          <ListItem 
            className={classes.li}
            sx={{ display: "block" }}
          >
               <ListItemText
                sx={{ marginLeft:2 }}
                primary={
                  <Typography className={classes.text} variant="h6">
                    <strong>{Vocabulary.glassGSM}</strong>
                  </Typography>
                }
              /> 
          </ListItem>
           {/* orders */}
        <ListItem
          className={classes.li}
          onClick={(event) =>
            handleNavigationChange(`${LocalUrlEnum.repairOrders}`)
          }
        >
          <ListItemButton
          
          >
            <ListItemIcon>
              <Description
                sx={{ color: theme.palette.primary?.main}}
                fontSize="large"
              />
               <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.repairOrders}
                  </Typography>
                }
              />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <ListItem
          className={classes.li}
          onClick={(event) =>
            handleNavigationChange(`${LocalUrlEnum.deviceRepairs}`)
          }
        >
          <ListItemButton

          >
            <ListItemIcon
              sx={{
                justifyContent: "right",
              }}
            >
              <Build
                sx={{ color: theme.palette.primary?.main }}
                fontSize="large"
              />
               <ListItemText
                primary={
                  <Typography className={classes.text} variant="h6">
                    {Vocabulary.deviceRepairs}
                  </Typography>
                }
              />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        </List>
      </>
    );
  }

  return (
    <>
      {matches && localStorageRole === roles.customer ? (
        <CustomerMainSliderDataMobile />
      ) : (
        <CustomDrawer
          variant={matches ? "temporary" : "permanent"}
          anchor="left"
          onClose={() => menuContext.setOpen(false)}
          ModalProps={matches ? { keepMounted: true } : undefined}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.dashboard?.main,
            },
          }}
          open={menuContext.open}
        >
          {showMenu()}
        </CustomDrawer>
      )}
    </>
  );
}

const styles = (theme: any) =>
  createStyles({
    container: {
      [theme.breakpoints.down("lg")]: {},
    },
    avatar: {
      backgroundColor: `${theme.palette.primary?.main} !important`,
    },
    text: { color: `${theme.palette.whiteColor?.main} !important` },
    li: {
      borderBottom: `1px solid ${theme.palette.dashboardLight?.main}`,
      padding: "5px 0 5px 0 !important",
    },
  });

export default withStyles(styles, { withTheme: true })(DrawerLayout);
